import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFamilyDetails } from "@/hooks/familyHooks";
import CreateFamilyForm from "./parts/CreateFamilyForm";
import JoinFamilyForm from "./parts/JoinFamilyForm";
import FamilyHeader from "./parts/FamilyHeader";
import HouseManager from "./parts/HouseManager";
import MembersManager from "./parts/MembersManager";
import InviteManager from "./parts/InviteManager";
import { useAuthContext } from "@/providers/AuthProvider";
import {
  Home as HomeIcon,
  Link as LinkIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`family-tabpanel-${index}`}
      aria-labelledby={`family-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const FamilyPage: React.FC = () => {
  const { user } = useAuthContext();
  const userRole = user?.role || "user";
  const [tabIndex, setTabIndex] = useState(0);
  const { data: family, isLoading, isError, refetch } = useFamilyDetails();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Si les données sont en cours de chargement
  if (isLoading) {
    return (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        gap={2}
        minHeight="50vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6">
          Chargement des informations de famille...
        </Typography>
      </Box>
    );
  }

  // Cas 1: L'utilisateur n'a pas de famille (roles: 'user' ou erreur API)
  if (isError || userRole === "user" || !family) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
          Gestion de famille
        </Typography>
        <Typography paragraph>
          Rejoignez ou créez une famille pour partager vos listes et gérer vos
          maisons ensemble.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={4}
              sx={{
                height: "100%",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: 8,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <CreateFamilyForm onSuccess={() => refetch()} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              elevation={4}
              sx={{
                height: "100%",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: 8,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <JoinFamilyForm onSuccess={() => refetch()} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Cas 2: L'utilisateur est membre d'une famille
  return (
    <Box p={3}>
      <FamilyHeader family={family} />

      <Card elevation={3} sx={{ mt: 3, overflow: "visible" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : undefined}
            sx={{
              "& .MuiTab-root": {
                fontWeight: "bold",
                py: 2,
              },
            }}
          >
            <Tab icon={<PersonIcon />} label="Membres" iconPosition="start" />
            <Tab icon={<HomeIcon />} label="Maisons" iconPosition="start" />
            <Tab icon={<LinkIcon />} label="Invitation" iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <TabPanel value={tabIndex} index={0}>
            <MembersManager
              family={family}
              isOwner={userRole === "familyOwner"}
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            <HouseManager
              family={family}
              isOwner={userRole === "familyOwner"}
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={2}>
            <InviteManager
              family={family}
              isOwner={userRole === "familyOwner"}
            />
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FamilyPage;
